import { ExternalLink, FolderOpen, SlidersHorizontal } from 'lucide-react';
import type { ReactElement } from 'react';
import { Navigate, useLocation } from 'react-router';

import { LinkCard, PageHeader } from '../design/components.js';

import { HubBlocs, TodoList } from './DomainPage.js';
import { AdministrationPlus, SpaceSwitch, useShell } from './Shell.js';
import { ancienHub, COMPTE, domainTodo, hubOf, type HubBloc } from './navigation.js';

import './hub.css';

/**
 * « Plus » de l'espace bénévole (comme l'onglet Plus de l'app) : Boutique (Pulsa-store et mes
 * commandes), Mon engagement (mes bons, projets collectifs), Participer (sondages, photos,
 * remontée), Matériel (emprunts, véhicules), Mon compte, puis les liens utiles (Cloud,
 * e-Protec). Seuls les écrans permis par le serveur sont proposés ; aucun écran de gestion.
 */
export function PlusPage(): ReactElement {
  const { shell } = useShell();
  const compte: HubBloc[] = hubOf('compte', shell.navigation).map((b) => ({
    ...b,
    key: 'compte',
    title: 'Mon compte',
    cards: [
      ...b.cards,
      {
        section: 'Mon compte',
        label: 'Préférences',
        path: COMPTE.path,
        icon: 'sliders-horizontal',
        description: 'Thème, compte et sécurité, déconnexion.',
        tabs: [],
      },
    ],
  }));
  const blocs = [...hubOf('plus', shell.navigation), ...compte];
  const todo = domainTodo('plus', shell.tiles);
  const externes = shell.actions.filter((a) => a.path.startsWith('https://') && !a.gestion);
  const liens = shell.liens ?? [];
  return (
    <div className="o-stack o-hub o-hub--plus">
      <PageHeader
        title="Plus"
        description="Boutique, engagement, sondages, photos, matériel, liens utiles et Mon compte."
      />
      <SpaceSwitch variant="plus" />
      <AdministrationPlus />
      {todo.length > 0 && <TodoList tiles={todo} label="À faire" />}
      {/* Blocs en colonnes denses (retour du référent rc.12 : trop de vide bout à bout), les
          liens utiles rangés avec eux. */}
      <HubBlocs blocs={blocs} dense>
        {(liens.length > 0 || externes.length > 0) && (
          <section className="o-hub-bloc" aria-labelledby="bloc-liens">
            <h2 id="bloc-liens" className="o-hub-bloc__title">
              Liens utiles
            </h2>
            <div className="o-hub-cards">
              {[
                ...liens.map((l) => ({
                  key: l.key,
                  label: l.label,
                  url: l.url,
                  hint: 'Mes fichiers sur Nextcloud, dans un nouvel onglet.',
                  Icon: FolderOpen,
                })),
                ...externes.map((a) => ({
                  key: a.key,
                  label: a.label,
                  url: a.path,
                  hint: 'e-Protec, dans un nouvel onglet.',
                  Icon: ExternalLink,
                })),
              ].map((l) => (
                <article key={l.key} className="o-link-card">
                  <a
                    className="o-link-card__main"
                    href={l.url}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    <span className="o-link-card__icon" aria-hidden="true">
                      <l.Icon size={20} />
                    </span>
                    <span className="o-link-card__text">
                      <span className="o-link-card__title">{l.label}</span>
                      <span className="o-link-card__description">{l.hint}</span>
                    </span>
                  </a>
                </article>
              ))}
            </div>
          </section>
        )}
      </HubBlocs>
      {blocs.length === 0 && (
        <LinkCard
          title="Mon compte"
          description="Ma fiche, mes préférences."
          icon={SlidersHorizontal}
          to={COMPTE.path}
        />
      )}
    </div>
  );
}

/**
 * Anciennes pages de domaine (0.4.0-rc.6 : /activites, /materiel, /crise…) : l'adresse reste
 * valide et mène à la rubrique qui la remplace.
 */
export function AncienHub(): ReactElement {
  const { shell } = useShell();
  const { pathname } = useLocation();
  return <Navigate replace to={ancienHub(pathname, shell.navigation) ?? '/'} />;
}
